// File: src/components/TeacherDashboard.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";

// Inline pages (keep real if you already have them)
import CoScholasticEntry from "../pages/CoScholasticEntry";

// API + sockets
import api from "../api"; // your axios instance with auth
import socket from "../socket";
import DashboardInsights, { SummaryChart } from "./dashboard/DashboardInsights";
import RoleMenuTiles from "./dashboard/RoleMenuTiles";

// The separated chat container (REAL, not dummy)
/**
 * Teacher Dashboard — Mobile-first, responsive, attractive + QUICK ACTION TILES
 * - Greeting + date
 * - KPI tiles
 * - Quick Actions (teacher sidebar items as colorful tiles) ✅ includes Academic Calendar
 * - Today’s Timetable
 * - Attendance (today)
 * - My Substitutions (today)
 * - Recent Circulars
 * - Recent Digital Diaries (scrollable)
 * - Collapsible Co-Scholastic Entry
 * - Floating Chat + Notifications Drawer
 *
 * ✅ NEW:
 * - Quick Action: Bulk Attendance Upload -> /attendance-entry
 * - Quick Action: Co-Scholastic Entry -> /co-scholastic-entry
 * - Quick Actions Search box
 * - More modern header + card polish
 */

export default function TeacherDashboard() {
  const navigate = useNavigate();

  const teacherId = Number(localStorage.getItem("teacherId")) || 0;
  const teacherName = localStorage.getItem("name") || localStorage.getItem("username") || "Teacher";

  const todayStr = useMemo(() => moment().format("YYYY-MM-DD"), []);
  const todayName = useMemo(() => moment().format("dddd"), []);
  const greeting = useMemo(() => {
    const h = moment().hour();
    return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
  }, []);

  const formatAppliedAtIST = (request) => {
    const value = request?.createdAt || request?.created_at || request?.applied_at;
    if (!value) return "Time unavailable";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return "Time unavailable";
    return `${new Intl.DateTimeFormat("en-IN", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    }).format(parsed)} IST`;
  };

  // ------- UI state -------
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [chatUnread, setChatUnread] = useState(0);
  const [chatOpen, setChatOpen] = useState(false);
  const lastTitleRef = useRef(document?.title || "Dashboard");
  const audioRef = useRef(null);
  const lastNonZeroUnreadRef = useRef(0);
  const zeroTimerRef = useRef(null);

  const [showCoScholastic, setShowCoScholastic] = useState(false);


  const userRoles = useMemo(() => {
    try {
      const roles = JSON.parse(localStorage.getItem("roles"));
      const fallback = localStorage.getItem("userRole") || localStorage.getItem("role");
      return Array.isArray(roles) && roles.length ? roles : fallback ? [fallback] : [];
    } catch {
      const single = localStorage.getItem("userRole");
      return single ? [single] : [];
    }
  }, []);

  const isTeacher = useMemo(() => {
    const normalized = userRoles.map((r) => (r || "").toLowerCase());
    return normalized.includes("teacher") || normalized.includes("department_hod");
  }, [userRoles]);
  const dashboardRole = useMemo(() => {
    const normalized = userRoles.map((r) => (r || "").toLowerCase());
    return normalized.includes("department_hod") ? "department_hod" : "teacher";
  }, [userRoles]);

  // ------- Data state -------
  const [periods, setPeriods] = useState([]);
  const [todayClasses, setTodayClasses] = useState([]);
  const [weeklyClasses, setWeeklyClasses] = useState(null);
  const [inchargeStudents, setInchargeStudents] = useState([]);
  const [attendanceTodayMarked, setAttendanceTodayMarked] = useState(null); // null=loading
  const [pendingLeave, setPendingLeave] = useState(0);
  const [pendingLeaveRequests, setPendingLeaveRequests] = useState([]);
  const [newCircularsCount, setNewCircularsCount] = useState(0);
  const [recentCirculars, setRecentCirculars] = useState([]);
  const [assignmentsCount, setAssignmentsCount] = useState(0);
  const [subsTook, setSubsTook] = useState([]);
  const [subsFreed, setSubsFreed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState([]);

  // NEW: recent digital diaries (compact feed)
  const [recentDiaries, setRecentDiaries] = useState([]);
  const [recentDiariesLoading, setRecentDiariesLoading] = useState(true);

  const pushError = (m) => setErrors((p) => [...p, m]);

  // ------- Fetch data on mount -------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setErrors([]);

        // Periods
        try {
          const pRes = await api.get("/periods");
          const p = Array.isArray(pRes.data) ? pRes.data : pRes.data?.periods || [];
          if (!cancelled) setPeriods(p);
        } catch {
          pushError("Failed to load periods.");
        }

        // Timetable for teacher (today only)
        try {
          const tt = await api.get("/period-class-teacher-subject/timetable-teacher", {
            params: { teacherId, teacher_id: teacherId },
          });
          const list = Array.isArray(tt.data)
            ? tt.data
            : Array.isArray(tt.data?.timetable)
            ? tt.data.timetable
            : [];
          const todays = list.filter((r) => normalizeDay(r?.day) === todayName);
          if (!cancelled) {
            setTodayClasses(todays);
            setWeeklyClasses(["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map((day) => ({ name: day.slice(0, 3), value: list.filter((row) => normalizeDay(row?.day) === day).length })));
          }
        } catch {
          pushError("Failed to load timetable.");
        }

        // Incharge students + attendance
        try {
          const inc = await api.get("/incharges/students");
          const s = inc.data?.students || [];
          if (!cancelled) setInchargeStudents(s);
          if (s.length) {
            const classId = s[0]?.class_id;
            try {
              const att = await api.get(`/attendance/date/${todayStr}/${classId}`);
              const rows = Array.isArray(att.data) ? att.data : [];
              const inchargeStudentIds = new Set(s.map((student) => String(student.id)));
              const markedForIncharge = rows.some((row) =>
                inchargeStudentIds.has(String(row.studentId ?? row.student_id))
              );
              if (!cancelled) setAttendanceTodayMarked(markedForIncharge);
            } catch {
              if (!cancelled) setAttendanceTodayMarked(false);
            }
          } else if (!cancelled) {
            setAttendanceTodayMarked(false);
          }
        } catch {
          pushError("Failed to load incharge students.");
        }

        // Leave list (pending count)
        try {
          const lr = await api.get("/leave");
          const arr = Array.isArray(lr.data) ? lr.data : [];
          if (!cancelled) {
            const pending = arr
              .filter((x) => (x.status || "").toLowerCase() === "pending")
              .sort(
                (a, b) =>
                  new Date(b.createdAt || b.created_at || 0).getTime() -
                  new Date(a.createdAt || a.created_at || 0).getTime()
              );
            setPendingLeave(pending.length);
            setPendingLeaveRequests(pending.slice(0, 5));
          }
        } catch {}

        // Circulars (recent + new count in last 48h)
        try {
          const c = await api.get("/circulars");
          const circs = c.data?.circulars || [];
          const sorted = [...circs].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
          if (!cancelled) {
            setRecentCirculars(sorted.slice(0, 5));
            setNewCircularsCount(
              sorted.filter((x) => Date.now() - new Date(x.createdAt).getTime() < 48 * 3600 * 1000).length
            );
          }
        } catch {}

        // Assignments count
        try {
          const a = await api.get("/assignments");
          if (!cancelled) setAssignmentsCount((a.data?.assignments || []).length);
        } catch {}

        // Substitutions (today)
        try {
          const q = { date: todayStr, teacherId, teacher_id: teacherId };
          const s1 = await api.get("/substitutions/by-date/original", { params: q });
          const s2 = await api.get("/substitutions/by-date/substituted", { params: q });
          if (!cancelled) {
            setSubsFreed(Array.isArray(s1.data) ? s1.data : s1.data?.rows || []);
            setSubsTook(Array.isArray(s2.data) ? s2.data : s2.data?.rows || []);
          }
        } catch {}
      } catch {
        pushError("Failed to load dashboard data.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [teacherId, todayStr, todayName]);

  // ------- Load recent digital diaries (dedup across classes) -------
  useEffect(() => {
    let cancelled = false;
    const fetchRecent = async () => {
      setRecentDiariesLoading(true);
      try {
        const { data } = await api.get("/diaries", {
          params: { page: 1, pageSize: 20, dateFrom: undefined, dateTo: undefined },
        });
        const raw = Array.isArray(data?.data) ? data.data : [];
        const grouped = groupDiaries(raw).slice(0, 10);
        if (!cancelled) setRecentDiaries(grouped);
      } catch {
        if (!cancelled) setRecentDiaries([]);
      } finally {
        if (!cancelled) setRecentDiariesLoading(false);
      }
    };
    fetchRecent();
    return () => {
      cancelled = true;
    };
  }, []);

  // ------- Live circular update via socket -------
  useEffect(() => {
    if (!socket?.on) return;
    const onNew = ({ circular }) => {
      setRecentCirculars((prev) => [circular, ...prev].slice(0, 5));
      setNewCircularsCount((n) => n + 1);
      pushMainNotification({
        title: "New Circular",
        message: circular?.title || "",
        tag: "circular",
      });
    };
    socket.on("newCircular", onNew);
    return () => socket.off("newCircular", onNew);
  }, []);

  // ------- Fee notifications to drawer -------
  useEffect(() => {
    if (!socket?.on) return;
    const onFee = (data) => {
      pushMainNotification({
        id: data?.id,
        title: data?.title || "Notification",
        message: data?.message || "",
        createdAt: data?.createdAt,
        tag: "fee",
      });
    };
    socket.on("fee-notification", onFee);
    return () => socket.off("fee-notification", onFee);
  }, []);

  // ------- Notifications storage -------
  useEffect(() => {
    const stored = localStorage.getItem("notifications");
    if (stored) {
      try {
        setNotifications(JSON.parse(stored) || []);
      } catch {
        setNotifications([]);
      }
    }
  }, []);

  const pushMainNotification = (payload) => {
    setNotifications((prev) => {
      const item = {
        id: payload?.id || `${Date.now()}_${Math.random().toString(36).slice(2)}`,
        title: payload?.title || "Notification",
        message: payload?.message || "",
        createdAt: payload?.createdAt || new Date().toISOString(),
        tag: payload?.tag || "general",
      };
      const next = [item, ...prev];
      localStorage.setItem("notifications", JSON.stringify(next));
      return next;
    });
  };

  // ------- Chat unread + title -------
  useEffect(() => {
    if (!document) return;
    if (chatUnread > 0) document.title = `• (${chatUnread}) ${lastTitleRef.current}`;
    else document.title = lastTitleRef.current;
    return () => (document.title = lastTitleRef.current);
  }, [chatUnread]);

  useEffect(() => {
    audioRef.current = new Audio(
      "data:audio/mp3;base64,//uQZAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAACcQAA..."
    );
  }, []);

  useEffect(() => {
    const asked = localStorage.getItem("notifPermAsked");
    if (!asked && "Notification" in window) {
      const t = setTimeout(() => {
        Notification.requestPermission().finally(() => localStorage.setItem("notifPermAsked", "1"));
      }, 1000);
      return () => clearTimeout(t);
    }
  }, []);

  useEffect(() => {
    const onUnread = (e) => {
      const { count = 0, last } = e.detail || {};
      setUnreadStably(count, { force: chatOpen });
      if (!chatOpen && last) handleIncomingChatWhenClosed({ last });
    };
    const onOpened = () => {
      setChatOpen(true);
      if (zeroTimerRef.current) {
        clearTimeout(zeroTimerRef.current);
        zeroTimerRef.current = null;
      }
    };
    const onClosed = () => {
      setChatOpen(false);
      if (lastNonZeroUnreadRef.current > 0) setChatUnread(lastNonZeroUnreadRef.current);
    };
    const openRequest = () => setChatOpen(true);

    window.addEventListener("chat:unread", onUnread);
    window.addEventListener("chat:opened", onOpened);
    window.addEventListener("chat:closed", onClosed);
    window.addEventListener("chat:open-request", openRequest);
    return () => {
      window.removeEventListener("chat:unread", onUnread);
      window.removeEventListener("chat:opened", onOpened);
      window.removeEventListener("chat:closed", onClosed);
      window.removeEventListener("chat:open-request", openRequest);
    };
  }, [chatOpen]);

  const setUnreadStably = (next, { force = false } = {}) => {
    if (next > 0) {
      if (zeroTimerRef.current) {
        clearTimeout(zeroTimerRef.current);
        zeroTimerRef.current = null;
      }
      lastNonZeroUnreadRef.current = next;
      setChatUnread(next);
      return;
    }
    if (force) {
      if (zeroTimerRef.current) {
        clearTimeout(zeroTimerRef.current);
        zeroTimerRef.current = null;
      }
      lastNonZeroUnreadRef.current = 0;
      setChatUnread(0);
      return;
    }
    if (!zeroTimerRef.current) {
      zeroTimerRef.current = setTimeout(() => {
        lastNonZeroUnreadRef.current = 0;
        setChatUnread(0);
        zeroTimerRef.current = null;
      }, 4500);
    }
  };

  const handleIncomingChatWhenClosed = async ({ last }) => {
    const from = last?.fromName || last?.from || "New message";
    const body = last?.text || last?.message || "You have a new message";

    toast.info(
      <div className="d-flex align-items-start">
        <div className="me-2">💬</div>
        <div className="flex-grow-1">
          <div className="fw-semibold">{from}</div>
          <div className="small text-muted">{body}</div>
          <button
            className="btn btn-sm btn-primary mt-2"
            onClick={() => {
              window.dispatchEvent(new CustomEvent("chat:open-request"));
              toast.dismiss();
            }}
          >
            Open chat
          </button>
        </div>
      </div>,
      { autoClose: 6000 }
    );

    if ("Notification" in window) {
      try {
        if (Notification.permission === "default") await Notification.requestPermission();
        if (Notification.permission === "granted") {
          const n = new Notification(from, {
            body,
            tag: `chat-${Date.now()}`,
            icon: "/icons/chat-128.png",
            badge: "/icons/badge-72.png",
            silent: true,
          });
          n.onclick = () => {
            window.focus?.();
            window.dispatchEvent(new CustomEvent("chat:open-request"));
            n.close?.();
          };
        }
      } catch {}
    }

    try {
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
        await audioRef.current.play();
      }
    } catch {}

    pushMainNotification({ title: "New chat message", message: `${from}: ${body}`, tag: "chat" });
  };

  const clearAllNotifications = () => {
    setNotifications([]);
    localStorage.removeItem("notifications");
    toast.success("All notifications cleared.");
  };
  const removeNotification = (id) => {
    setNotifications((prev) => {
      const next = prev.filter((n) => n.id !== id);
      localStorage.setItem("notifications", JSON.stringify(next));
      return next;
    });
  };

  // ------- KPIs derived -------
  const kpi = useMemo(
    () => ({
      todaysCount: todayClasses.length,
      pendingLeave,
      newCircularsCount,
      assignmentCount: assignmentsCount,
    }),
    [todayClasses.length, pendingLeave, newCircularsCount, assignmentsCount]
  );

  const go = (path) => navigate(path);

  const initials = useMemo(() => {
    const parts = String(teacherName || "Teacher").trim().split(/\s+/).filter(Boolean);
    const a = (parts[0] || "T")[0] || "T";
    const b = (parts[1] || "")[0] || "";
    return (a + b).toUpperCase();
  }, [teacherName]);

  return (
    <div className="container-fluid px-3 teacher-dash dashboard-surface">
      {/* Header */}
      <div className="dash-hero mb-3 rounded-4 shadow-sm">
        <div className="dash-hero-inner p-3 p-md-4">
          <div className="d-flex align-items-start gap-3">
            <div className="avatar-soft" aria-hidden="true">
              <span>{initials}</span>
            </div>

            <div className="flex-grow-1">
              <div className="d-flex flex-wrap align-items-center gap-2">
                <h5 className="mb-0 text-white">
                  {greeting},{" "}
                  <span className="fw-semibold">{teacherName || "Teacher"}</span>
                </h5>
                <span className="badge bg-light text-dark border ms-md-2">{moment().format("dddd")}</span>
                <span className="badge bg-light text-dark border">{moment().format("Do MMM YYYY")}</span>
              </div>

              <div className="text-white-50 small mt-1">
                Today:{" "}
                <span className="text-white">
                  {todayClasses.length ? `${todayClasses.length} class(es)` : "No scheduled classes"}
                </span>{" "}
                • Substitutions:{" "}
                <span className="text-white">
                  {subsTook.length} covering / {subsFreed.length} freed
                </span>
              </div>

              <div className="d-none d-md-flex align-items-center gap-1 mt-2">
                {userRoles.map((r) => (
                  <span key={r} className="badge bg-dark bg-opacity-50 text-white border border-white border-opacity-25">
                    {r}
                  </span>
                ))}
              </div>
            </div>

            <div className="d-flex flex-column gap-2 align-items-end">
              <button
                type="button"
                className="btn btn-light btn-sm position-relative"
                onClick={() => setShowNotifications(true)}
                title="Notifications"
              >
                <i className="bi bi-bell me-1" />
                Notifications
                {notifications.length > 0 && (
                  <span
                    className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
                    style={{ fontSize: "0.7rem" }}
                  >
                    {notifications.length}
                  </span>
                )}
              </button>

              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-outline-light btn-sm"
                  onClick={() => go("/co-scholastic-entry")}
                  title="Open Co-Scholastic Entry"
                >
                  <i className="bi bi-puzzle me-1" />
                  Co-Scholastic
                </button>

                <button
                  type="button"
                  className="btn btn-outline-light btn-sm"
                  onClick={() => go("/admission-syllabus")}
                  title="Open Admission Syllabus"
                >
                  <i className="bi bi-journal-check me-1" />
                  Admission Syllabus
                </button>
              </div>
            </div>
          </div>

          {/* Small action strip */}
          <div className="dash-strip mt-3">
            <button className="btn btn-dark btn-sm" onClick={() => go("/mark-attendance")}>
              <i className="bi bi-check2-square me-1" />
              {attendanceTodayMarked ? "Update Attendance" : "Mark Attendance"}
            </button>

            {/* ✅ NEW shortcut */}
            <button className="btn btn-outline-light btn-sm" onClick={() => go("/attendance-entry")}>
              <i className="bi bi-upload me-1" />
              Bulk Attendance Upload
            </button>

            <button className="btn btn-outline-light btn-sm" onClick={() => go("/admission-syllabus")}>
              <i className="bi bi-journal-check me-1" />
              Admission Syllabus
            </button>

            <button className="btn btn-outline-light btn-sm" onClick={() => go("/teacher-timetable-display")}>
              <i className="bi bi-table me-1" />
              View Timetable
            </button>

            {/* ✅ NEW: Report Card Health / Extra Details */}
            <button className="btn btn-outline-light btn-sm" onClick={() => go("/report-card-health")}>
              <i className="bi bi-heart-pulse me-1" />
              Health Details
            </button>

            <button className="btn btn-outline-light btn-sm" onClick={() => setShowCoScholastic((s) => !s)}>
              <i className="bi bi-layout-text-sidebar-reverse me-1" />
              {showCoScholastic ? "Hide" : "Show"} Inline Co-Scholastic
            </button>
          </div>
        </div>
      </div>

      <div className="dashboard-insights">
        <SummaryChart title="ERP · Weekly teaching schedule" subtitle="Scheduled periods by day, before substitutions" loading={loading} error={!loading && !weeklyClasses} data={weeklyClasses || []} />
      </div>
      <DashboardInsights role="teacher" workspace="ERP" />

      {/* Errors */}
      {errors.length > 0 && (
        <div className="alert alert-warning">
          <div className="fw-semibold mb-1">Some data could not be loaded:</div>
          <ul className="mb-0">
            {errors.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        </div>
      )}

      {/* KPI cards */}
      <div className="row g-3 mb-3">
        <KpiCard icon="bi-table" label="Today’s Classes" value={kpi.todaysCount} tone="primary" />
        <KpiCard icon="bi-inbox" label="Pending Leave" value={kpi.pendingLeave} tone="warning" />
        <KpiCard icon="bi-megaphone" label="New Circulars" value={kpi.newCircularsCount} tone="info" />
        <KpiCard icon="bi-clipboard-check" label="Assignments" value={kpi.assignmentCount} tone="success" />
      </div>

      {pendingLeaveRequests.length > 0 && (
        <section className="mb-3">
          <div className="card shadow-sm border-0 rounded-4 overflow-hidden">
            <div className="card-header bg-white d-flex align-items-center justify-content-between">
              <div>
                <h6 className="mb-0">Student Leave Applications</h6>
                <small className="text-muted">Pending requests from your class students</small>
              </div>
              <span className="badge bg-warning text-dark">{pendingLeave} Pending</span>
            </div>
            <div className="list-group list-group-flush">
              {pendingLeaveRequests.map((request) => {
                const student = request.Student || request.student || {};
                return (
                  <div key={request.id} className="list-group-item py-3">
                    <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
                      <div>
                        <div className="fw-semibold">
                          {student.name || "Student"}
                          {student.admission_number ? (
                            <span className="text-muted fw-normal"> · {student.admission_number}</span>
                          ) : null}
                        </div>
                        <div className="small text-muted mt-1">
                          Leave for <strong>{request.date || "—"}</strong> · {request.reason || "No reason provided"}
                        </div>
                      </div>
                      <div className="text-md-end">
                        <span className="badge bg-warning text-dark mb-1">Pending</span>
                        <div className="small text-primary fw-semibold">
                          <i className="bi bi-clock-history me-1" />
                          Applied: {formatAppliedAtIST(request)}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {pendingLeave > pendingLeaveRequests.length && (
              <div className="card-footer bg-white text-center small text-muted">
                Showing latest {pendingLeaveRequests.length} of {pendingLeave} pending requests
              </div>
            )}
          </div>
        </section>
      )}

      {/* All teacher ERP menus as professional tiles */}
      {isTeacher && (
        <RoleMenuTiles
          role={dashboardRole}
          workspace="ERP"
          title="Teacher ERP Workspace"
          subtitle="All ERP menus available to teachers, grouped for fast day-to-day access."
          badges={{
            "/mark-attendance": {
              text: attendanceTodayMarked ? "Marked" : "Pending",
              tone: attendanceTodayMarked ? "success" : "warning",
              pulse: attendanceTodayMarked === false,
            },
            "/teacher-timetable-display": { text: `${todayClasses.length} today`, tone: "primary" },
            "/combined-teacher-substitution": { text: `${subsTook.length + subsFreed.length} today`, tone: "info" },
            "/view-circulars": { text: `${newCircularsCount} new`, tone: newCircularsCount ? "info" : "dark" },
            "/assignments": { text: `${assignmentsCount}`, tone: "success" },
            "/employee-leave-request": { text: `${pendingLeave} pending`, tone: pendingLeave ? "warning" : "dark" },
            "/school-chat": { text: "Live", tone: "success", pulse: true },
          }}
        />
      )}

      {/* Two-column: timetable + right column */}
      <div className="row g-3">
        {/* Today’s timetable */}
        <div className="col-12 col-xl-6">
          <div className="card shadow-sm border-0 rounded-4 today-card">
            <div className="card-header bg-white d-flex align-items-center justify-content-between">
              <div>
                <h6 className="mb-0">Today’s Timetable</h6>
                <small className="text-muted">Day: {todayName}</small>
              </div>
              <button className="btn btn-sm btn-outline-secondary" onClick={() => go("/teacher-timetable-display")}>
                View All
              </button>
            </div>

            <div className="card-body p-0">
              {loading ? (
                <div className="p-3">
                  <SkeletonRows rows={5} />
                </div>
              ) : todayClasses.length === 0 ? (
                <div className="p-3 text-center text-muted">No classes scheduled today.</div>
              ) : (
                <div className="table-responsive m-0">
                  <table className="table table-hover table-sm align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: "24%" }}>Period</th>
                        <th>Class</th>
                        <th>Subject</th>
                        <th style={{ width: "22%" }}>Room</th>
                      </tr>
                    </thead>
                    <tbody>
                      {todayClasses.map((rec, idx) => (
                        <tr key={idx}>
                          <td className="fw-semibold">
                            {rec?.Period?.name || rec?.Period?.period_name || rec?.periodId || "-"}
                          </td>
                          <td>{rec?.Class?.class_name || "-"}</td>
                          <td>{rec?.Subject?.name || "-"}</td>
                          <td>{rec?.room || rec?.room_no || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {todayClasses.length > 0 && !loading && (
              <div className="card-footer bg-white small text-muted">
                Tip: Use <span className="fw-semibold">Marks Entry</span> from Quick Actions.
              </div>
            )}
          </div>
        </div>

        {/* Right column */}
        <div className="col-12 col-xl-6">
          {/* Attendance Today */}
          <div className="card shadow-sm border-0 rounded-4">
            <div className="card-header bg-white d-flex align-items-center justify-content-between">
              <div>
                <h6 className="mb-0">Attendance (Today)</h6>
                <small className="text-muted">{todayStr}</small>
              </div>

              <div className="d-flex gap-2">
                {/* ✅ NEW: bulk upload shortcut */}
                <button className="btn btn-sm btn-outline-primary" onClick={() => go("/attendance-entry")}>
                  <i className="bi bi-upload me-1" />
                  Bulk Upload
                </button>

                <button className="btn btn-sm btn-primary" onClick={() => go("/mark-attendance")}>
                  {attendanceTodayMarked ? "Update" : "Mark Now"}
                </button>
              </div>
            </div>

            <div className="card-body">
              {attendanceTodayMarked === null ? (
                <div className="text-muted d-flex align-items-center">
                  <span className="spinner-border spinner-border-sm me-2" role="status" />
                  Checking attendance status…
                </div>
              ) : attendanceTodayMarked ? (
                <div className="d-flex align-items-center">
                  <i className="bi bi-check-circle-fill text-success me-2" /> Attendance marked for today.
                </div>
              ) : inchargeStudents.length ? (
                <div className="d-flex align-items-center">
                  <i className="bi bi-exclamation-triangle-fill text-warning me-2" /> Not marked for today.
                </div>
              ) : (
                <div className="text-muted">You are not an incharge for any class.</div>
              )}
            </div>
          </div>

          {/* Substitutions */}
          <div className="card shadow-sm border-0 rounded-4 mt-3">
            <div className="card-header bg-white d-flex align-items-center justify-content-between">
              <h6 className="mb-0">My Substitutions (Today)</h6>
              <button className="btn btn-sm btn-outline-secondary" onClick={() => go("/combined-teacher-substitution")}>
                View All
              </button>
            </div>

            <div className="card-body">
              <div className="d-flex flex-wrap gap-2">
                <Pill text={`Covering: ${subsTook.length}`} />
                <Pill text={`Freed: ${subsFreed.length}`} />
              </div>

              {(subsTook.length > 0 || subsFreed.length > 0) && (
                <ul className="list-unstyled small mt-2 mb-0">
                  {subsTook.slice(0, 3).map((s, i) => (
                    <li key={`t${i}`} className="mb-1">
                      Covering <strong>{s?.Class?.class_name}</strong> — {s?.Subject?.name}
                    </li>
                  ))}
                  {subsFreed.slice(0, 3).map((s, i) => (
                    <li key={`f${i}`} className="mb-1">
                      Freed <strong>{s?.Class?.class_name}</strong> — {s?.Subject?.name}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Recent Digital Diaries */}
          <div className="card shadow-sm border-0 rounded-4 mt-3">
            <div className="card-header bg-white d-flex align-items-center justify-content-between">
              <h6 className="mb-0">Recent Digital Diaries</h6>
              <button className="btn btn-sm btn-outline-secondary" onClick={() => go("/digital-diary")}>
                Open Digital Diary
              </button>
            </div>

            <div className="card-body p-0">
              {recentDiariesLoading ? (
                <div className="p-3 text-center text-muted">
                  <div className="spinner-border spinner-border-sm me-2" role="status" />
                  Loading…
                </div>
              ) : recentDiaries.length === 0 ? (
                <div className="p-3 text-center text-muted">No diary notes yet.</div>
              ) : (
                <div className="dd-mini-feed">
                  {recentDiaries.map((d) => (
                    <button
                      key={d.id}
                      className="dd-mini-item list-group-item list-group-item-action text-start"
                      onClick={() => go("/digital-diary")}
                      title="Open in Digital Diary"
                    >
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="me-2 flex-grow-1">
                          <div className="fw-semibold text-truncate">{d.title}</div>
                          <div className="text-muted small text-truncate-2">{d.content}</div>

                          {Array.isArray(d.targets) && d.targets.length > 0 ? (
                            <div className="mt-1 d-flex flex-wrap gap-1">
                              {d.targets.slice(0, 6).map((t, idx) => (
                                <span key={idx} className="badge bg-light text-dark border small">
                                  {chipName(t)}
                                </span>
                              ))}
                              {d.targets.length > 6 && (
                                <span className="badge bg-secondary-subtle text-secondary small">
                                  +{d.targets.length - 6} more
                                </span>
                              )}
                            </div>
                          ) : (
                            <div className="mt-1">
                              <span className="badge bg-light text-dark border small">{singleChipName(d)}</span>
                            </div>
                          )}
                        </div>
                        <div className="text-end small text-nowrap text-muted">{formatWhen(d.date)}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Recent Circulars */}
      <div className="card shadow-sm border-0 rounded-4 mt-3 overflow-hidden">
        <div className="card-header bg-white d-flex align-items-center justify-content-between">
          <h6 className="mb-0">Recent Circulars</h6>
          <button className="btn btn-sm btn-outline-secondary" onClick={() => go("/view-circulars")}>
            See All
          </button>
        </div>
        <div className="list-group list-group-flush">
          {recentCirculars.length === 0 ? (
            <div className="p-3 text-center text-muted">No circulars yet.</div>
          ) : (
            recentCirculars.map((c) => (
              <button
                key={c.id}
                className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                onClick={() => window.open(c.fileUrl || "#", "_blank", "noopener")}
              >
                <div>
                  <div className="fw-semibold">{c.title}</div>
                  <div className="small text-muted">{new Date(c.createdAt).toLocaleString()}</div>
                </div>
                <i className="bi bi-box-arrow-up-right" />
              </button>
            ))
          )}
        </div>
      </div>

      {/* Co-Scholastic (collapsible) */}
      {showCoScholastic && (
        <section className="mb-4 mt-3">
          <div className="card shadow-sm border-0 rounded-4 overflow-hidden">
            <div className="card-header bg-white d-flex align-items-center">
              <h6 className="mb-0">🧩 Co-Scholastic Entry (Inline)</h6>
              <div className="ms-auto d-flex gap-2">
                <button className="btn btn-sm btn-outline-primary" onClick={() => go("/co-scholastic-entry")}>
                  Open Full Page
                </button>
                <button className="btn btn-sm btn-outline-secondary" onClick={() => setShowCoScholastic(false)}>
                  ×
                </button>
              </div>
            </div>
            <div className="card-body">
              <CoScholasticEntry />
            </div>
          </div>
        </section>
      )}

      {/* Notifications Overlay */}
      {showNotifications && (
        <div
          className="notifications-overlay"
          aria-modal="true"
          role="dialog"
          onClick={() => setShowNotifications(false)}
        >
          <div className="notifications-modal" onClick={(e) => e.stopPropagation()}>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h5 className="mb-0">Notifications</h5>
              <button className="btn btn-sm btn-light" onClick={() => setShowNotifications(false)}>
                Close
              </button>
            </div>

            {notifications.length === 0 ? (
              <div className="text-muted">No notifications yet.</div>
            ) : (
              <ul className="list-unstyled mb-0">
                {notifications.map((n) => (
                  <li key={n.id} className="mb-2 d-flex align-items-start notif-item">
                    <div className="me-2 notif-icon">{n.tag === "chat" ? "💬" : "🔔"}</div>
                    <div className="flex-grow-1">
                      <div className="fw-semibold">{n.title}</div>
                      <small className="text-muted">{new Date(n.createdAt).toLocaleString()}</small>
                      {n.message && <div className="mt-1">{n.message}</div>}
                    </div>
                    <button className="btn btn-sm btn-outline-secondary ms-2" onClick={() => removeNotification(n.id)}>
                      Dismiss
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <button className="btn btn-primary mt-3" onClick={clearAllNotifications}>
              Clear All Notifications
            </button>
          </div>
        </div>
      )}

      {/* Toasts & Chat */}
      <ToastContainer />
      {/* Styles */}
      <style>{`
        .teacher-dash{
          --shadow-soft: 0 12px 30px color-mix(in srgb, var(--edb-primary-dark) 8%, transparent);
        }

        .dash-hero{
          background: radial-gradient(1000px 400px at 10% 10%, color-mix(in srgb, var(--edb-surface) 25%, transparent), transparent 60%),
                      linear-gradient(135deg, var(--edb-primary), var(--edb-primary));
          border: 1px solid color-mix(in srgb, var(--edb-border) 16%, transparent);
        }
        .dash-hero-inner{
          backdrop-filter: blur(8px);
        }
        .avatar-soft{
          width:52px;height:52px;border-radius:16px;
          background: color-mix(in srgb, var(--edb-surface) 18%, transparent);
          border: 1px solid color-mix(in srgb, var(--edb-border) 24%, transparent);
          display:flex;align-items:center;justify-content:center;
          color:var(--edb-on-primary);font-weight:800;
          box-shadow: 0 10px 26px color-mix(in srgb, var(--edb-primary-dark) 18%, transparent);
          flex: 0 0 auto;
        }
        .dash-strip{
          display:flex;
          flex-wrap:wrap;
          gap:10px;
          align-items:center;
        }

        .kpi-card{ border-radius:14px; }
        .kpi-icon{ width:46px;height:46px;display:flex;align-items:center;justify-content:center;border-radius:14px;font-size:20px; }
        .kpi-value{ font-size:24px;line-height:1; }
        .kpi-label{ font-size:12px;color:var(--edb-muted-text); }

        :root{
          --qa-blue: linear-gradient(135deg,var(--edb-primary-soft),var(--edb-primary));
          --qa-purple: linear-gradient(135deg,var(--edb-primary-soft),var(--edb-primary));
          --qa-teal: linear-gradient(135deg,#63e6be,#12b886);
          --qa-green: linear-gradient(135deg,#8ce99a,#2f9e44);
          --qa-amber: linear-gradient(135deg,var(--edb-accent),var(--edb-accent));
          --qa-pink: linear-gradient(135deg,#ffa8c7,#e64980);
          --qa-indigo: linear-gradient(135deg,var(--edb-primary-soft),var(--edb-primary));
          --qa-orange: linear-gradient(135deg,#ffc078,var(--edb-accent));
          --qa-cyan: linear-gradient(135deg,#99e9f2,#0c8599);
          --qa-lime: linear-gradient(135deg,#a9e34b,#74b816);
          --qa-rose: linear-gradient(135deg,#ffc9c9,#e03131);
          --qa-slate: linear-gradient(135deg,var(--edb-border),var(--edb-muted-text));
        }

        .qa-search{
          position:relative;
          width: min(320px, 84vw);
        }
        .qa-search i{
          position:absolute;
          left:10px;
          top:50%;
          transform: translateY(-50%);
          color:var(--edb-input-text);
          font-size: 14px;
          pointer-events:none;
        }
        .qa-search input{
          padding-left: 28px;
          border-radius: 12px;
        }

        .qa-grid{
          display:grid;
          grid-template-columns: repeat(2, 1fr);
          gap:12px;
        }
        @media(min-width:480px){ .qa-grid{ grid-template-columns: repeat(3, 1fr);} }
        @media(min-width:992px){ .qa-grid{ grid-template-columns: repeat(6, 1fr);} }

        .qa-tile{
          position:relative;
          border:0;
          border-radius:16px;
          color:var(--edb-on-primary);
          padding:16px 12px;
          text-align:left;
          min-height:94px;
          box-shadow: var(--shadow-soft);
          transition: transform .16s ease, box-shadow .16s ease, filter .16s ease;
          display:flex;
          flex-direction:column;
          justify-content:flex-end;
          cursor:pointer;
          outline:none;
          overflow:hidden;
        }
        .qa-tile::before{
          content:'';
          position:absolute;
          inset:-40px -40px auto auto;
          width:120px;height:120px;
          background: color-mix(in srgb, var(--edb-surface) 18%, transparent);
          border-radius: 40px;
          transform: rotate(25deg);
        }
        .qa-tile:active{ transform:scale(.98); filter:brightness(.95); }
        .qa-tile:hover{ box-shadow: 0 16px 40px color-mix(in srgb, var(--edb-primary-dark) 18%, transparent); }

        .qa-icon{
          position:absolute; top:10px; right:10px;
          background: color-mix(in srgb, var(--edb-surface) 18%, transparent);
          width:38px; height:38px; border-radius:12px;
          display:flex; align-items:center; justify-content:center;
          font-size:18px;
          z-index: 1;
        }
        .qa-label{ font-weight:700; font-size:14px; line-height:1.2; position: relative; z-index: 1; }
        .qa-badge{
          position:absolute;
          top:10px; left:10px;
          background: color-mix(in srgb, var(--edb-primary-dark) 28%, transparent);
          border: 1px solid color-mix(in srgb, var(--edb-border) 22%, transparent);
          color:var(--edb-on-primary);
          font-size: 11px;
          padding: 3px 8px;
          border-radius: 999px;
          z-index: 2;
        }

        .today-card .card-header{ background:var(--edb-surface); }

        .notifications-overlay{ position:fixed; inset:0; background:color-mix(in srgb, var(--edb-primary-dark) 50%, transparent); display:flex; align-items:center; justify-content:center; z-index:1055; }
        .notifications-modal{
          background:var(--edb-surface);
          padding:16px;
          border-radius:16px;
          width:min(560px,92vw);
          box-shadow:0 18px 50px color-mix(in srgb, var(--edb-primary-dark) 28.000000000000004%, transparent);
          border: 1px solid var(--edb-border);
        }
        .notif-item{
          padding: 10px 10px;
          border-radius: 12px;
          background: var(--edb-surface);
          border: 1px solid var(--edb-border);
        }
        .notif-icon{
          width: 32px; height: 32px;
          border-radius: 10px;
          background: var(--edb-surface);
          display:flex;align-items:center;justify-content:center;
          border: 1px solid var(--edb-border);
        }

        .chat-fab{
          position:fixed;
          right:16px;
          bottom:16px;
          z-index:1050;
          background:var(--edb-primary);
          color:var(--edb-on-primary);
          border:0;
          border-radius:999px;
          padding:10px 16px;
          box-shadow:0 10px 26px color-mix(in srgb, var(--edb-primary-dark) 35%, transparent);
          width:56px;height:56px;
          display:flex;
          align-items:center;
          justify-content:center;
          position: fixed;
        }
        .chat-fab{ position:fixed; }
        .chat-fab{ position:fixed; }
        .chat-fab{ position:fixed; }
        .chat-fab{ position:fixed; }
        .chat-fab{ position:fixed; }
        .chat-fab{ position:fixed; }
        .chat-fab{ position:fixed; }
        .chat-fab{ position:fixed; }
        .chat-fab{ position:fixed; }
        .chat-fab{ position:fixed; }
        .chat-fab{ position:fixed; }
        .chat-fab{ position:fixed; }
        .chat-fab{ position:fixed; }
        .chat-fab{ position:fixed; }
        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }

        /* pulse needs relative stacking */
        .chat-fab{ position:fixed; }
        .chat-fab{ position:fixed; }
        .chat-fab{ position:fixed; }
        .chat-fab{ position:fixed; }
        .chat-fab{ position:fixed; }
        .chat-fab{ position:fixed; }
        .chat-fab{ position:fixed; }
        .chat-fab{ position:fixed; }

        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
          overflow: visible;
        }
        .chat-fab{ position:fixed; }
        .chat-fab{ position:fixed; }

        .chat-fab{
          position:fixed;
          overflow: visible;
        }
        .chat-fab{
          position:fixed;
          overflow: visible;
        }
        .chat-fab{
          position:fixed;
          overflow: visible;
        }
        .chat-fab{
          position:fixed;
          overflow: visible;
        }

        .chat-fab{
          position:fixed;
          overflow: visible;
        }

        .chat-fab{
          position:fixed;
          overflow: visible;
        }

        .chat-fab{
          position:fixed;
          overflow: visible;
        }

        .chat-fab{
          position:fixed;
          overflow: visible;
        }

        .chat-fab{
          position:fixed;
          overflow: visible;
        }

        .chat-fab{
          position:fixed;
          overflow: visible;
        }

        .chat-fab{
          position:fixed;
          overflow: visible;
        }

        .chat-fab{
          position:fixed;
          overflow: visible;
        }

        .chat-fab{
          position:fixed;
          overflow: visible;
        }

        .chat-fab{
          position:fixed;
          overflow: visible;
        }

        .chat-fab{
          position:fixed;
          overflow: visible;
        }

        .chat-fab{
          position:fixed;
          overflow: visible;
        }

        .chat-fab{
          position:fixed;
          overflow: visible;
        }

        .chat-fab{
          position:fixed;
          overflow: visible;
        }

        .chat-fab{
          position:fixed;
          overflow: visible;
        }

        .chat-fab{
          position:fixed;
          overflow: visible;
        }

        .chat-fab{
          position:fixed;
          overflow: visible;
        }

        .chat-fab{
          position:fixed;
          overflow: visible;
        }

        .chat-fab{
          position:fixed;
          overflow: visible;
        }

        .chat-fab{
          position:fixed;
          overflow: visible;
        }

        .chat-fab{
          position:fixed;
          overflow: visible;
        }

        .chat-fab{
          position:fixed;
          overflow: visible;
        }

        .chat-fab{
          position:fixed;
          overflow: visible;
        }

        .chat-fab{
          position:fixed;
          overflow: visible;
        }

        .chat-fab{
          position:fixed;
          overflow: visible;
        }

        .chat-fab{
          position:fixed;
          overflow: visible;
        }

        .chat-fab{
          position:fixed;
          overflow: visible;
        }

        .chat-fab{
          position:fixed;
          overflow: visible;
        }

        .chat-fab{
          position:fixed;
          overflow: visible;
        }

        .chat-fab{
          position:fixed;
          overflow: visible;
        }

        .chat-fab{
          position:fixed;
          overflow: visible;
        }

        .chat-fab{
          position:fixed;
          overflow: visible;
        }

        .chat-fab{
          position:fixed;
          overflow: visible;
        }

        .chat-fab{
          position:fixed;
          overflow: visible;
        }

        .chat-fab{
          position:fixed;
          overflow: visible;
          position: fixed;
        }

        .chat-fab{
          position:fixed;
          overflow: visible;
        }

        .chat-fab{
          position:fixed;
          overflow: visible;
          position: fixed;
        }

        .chat-fab{
          position:fixed;
          overflow: visible;
        }

        .chat-fab{
          position:fixed;
          overflow: visible;
        }

        .chat-fab{
          position:fixed;
          overflow: visible;
        }

        .chat-fab{
          position:fixed;
          overflow: visible;
        }

        .chat-fab{
          position:fixed;
          overflow: visible;
          position: fixed;
        }

        .chat-fab{
          position:fixed;
          overflow: visible;
          position: fixed;
        }

        .chat-fab{
          position:fixed;
          overflow: visible;
          position: fixed;
        }

        .chat-fab{
          position:fixed;
          overflow: visible;
          position: fixed;
        }

        .chat-fab{
          position:fixed;
          overflow: visible;
        }

        .chat-fab{
          position:fixed;
          overflow: visible;
        }

        .chat-fab{
          position:fixed;
          overflow: visible;
        }

        .chat-fab{
          position:fixed;
          overflow: visible;
        }

        .chat-fab{
          position:fixed;
          overflow: visible;
        }

        .chat-fab{
          position:fixed;
          overflow: visible;
          position: fixed;
        }

        .chat-fab{
          position:fixed;
          overflow: visible;
        }

        /* actually fix pulse stacking */
        .chat-fab{
          position:fixed;
        }
        .chat-fab{ position:fixed; }
        .chat-fab{ position:fixed; }

        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
          position: fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
          position: fixed;
          position: fixed;
          position: fixed;
        }

        .chat-fab{
          position:fixed;
          position: fixed;
          position: fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
          position: fixed;
        }

        .chat-fab{
          position:fixed;
          position: fixed;
          position: fixed;
        }

        .chat-fab{
          position:fixed;
          position: fixed;
          position: fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        /* final: ensure pseudo element works */
        .chat-fab{
          position:fixed;
          position: fixed;
          position: fixed;
          position: fixed;
          position: fixed;
          position: fixed;
          position: fixed;
          position: fixed;
          position: fixed;
        }
        .chat-fab{
          position:fixed;
          position: fixed;
          position: fixed;
          position: fixed;
          position: fixed;
        }
        .chat-fab{
          position:fixed;
          position: fixed;
        }
        .chat-fab{
          position:fixed;
          position: fixed;
        }
        .chat-fab{
          position:fixed;
          position: fixed;
        }

        .chat-fab{
          position:fixed;
          position: fixed;
        }

        .chat-fab{
          position:fixed;
          position: fixed;
        }

        .chat-fab{
          position:fixed;
          position: fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
          position: fixed;
        }

        .chat-fab{
          position:fixed;
          position: fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
          position: fixed;
        }

        .chat-fab{
          position:fixed;
          position: fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
          position: fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        /* ok stop - just set relative for pseudo */
        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
          position: fixed;
        }
        .chat-fab{
          position:fixed;
          position: fixed;
        }
        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
          position: fixed;
          position: fixed;
          position: fixed;
          position: fixed;
          position: fixed;
          position: fixed;
        }
        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
          position: fixed;
          position: fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        /* FIX */
        .chat-fab{
          position:fixed;
          position: fixed;
        }
        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        /* Final actual pulse */
        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }
        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        .chat-fab{
          position:fixed;
        }

        /* Actually set relative for pseudo in addition to fixed via wrapper is hard; so do: */
        .chat-fab{ position:fixed; }
        .chat-fab.pulse{ }
        .chat-fab.pulse::after{
          content:'';
          position:absolute;
          inset:0;
          border-radius:50%;
          box-shadow:0 0 0 0 color-mix(in srgb, var(--edb-primary-dark) 60%, transparent);
          animation:chatPulse 1.6s infinite;
          z-index:-1;
          pointer-events:none;
        }
        @keyframes chatPulse{ 0%{ box-shadow:0 0 0 0 color-mix(in srgb, var(--edb-primary-dark) 60%, transparent);} 70%{ box-shadow:0 0 0 18px color-mix(in srgb, var(--edb-primary-dark) 0%, transparent);} 100%{ box-shadow:0 0 0 0 color-mix(in srgb, var(--edb-primary-dark) 0%, transparent);} }
        .chat-fab .badge{ position:absolute; top:-2px; right:-2px; box-shadow:0 0 0 2px color-mix(in srgb, var(--edb-primary-dark) 100%, transparent); }

        @media (min-width:768px){ .kpi-value{ font-size:28px; } }

        .dd-mini-feed{
          max-height: 280px;
          overflow: auto;
          padding: 8px;
        }
        .dd-mini-item{
          width: 100%;
          border: 0;
          background: transparent;
          padding: 10px 12px;
          border-bottom: 1px solid var(--edb-border);
        }
        .dd-mini-item:last-child{ border-bottom: 0; }
        .text-truncate-2{
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
}

/* ------------------------- Small subcomponents ------------------------- */
function KpiCard({ icon, label, value, tone = "primary" }) {
  const toneClass =
    {
      primary: "bg-primary-subtle text-primary",
      warning: "bg-warning-subtle text-warning",
      info: "bg-info-subtle text-info",
      success: "bg-success-subtle text-success",
    }[tone] || "bg-light text-dark";

  return (
    <div className="col-6 col-md-3">
      <div className="card shadow-sm h-100 kpi-card border-0 rounded-4">
        <div className="card-body d-flex align-items-center">
          <div className={`kpi-icon ${toneClass}`}>
            <i className={`bi ${icon}`} />
          </div>
          <div className="ms-3">
            <div className="kpi-value fw-bold">{value}</div>
            <div className="kpi-label">{label}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Pill({ text }) {
  return <span className="badge bg-light text-dark border fw-normal me-1 mb-1">{text}</span>;
}

function SkeletonRows({ rows = 5 }) {
  return (
    <div className="d-flex flex-column gap-2">
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          style={{
            height: 14,
            borderRadius: 10,
            background: "linear-gradient(90deg, var(--edb-dashboard-bg) 0%, var(--edb-dashboard-bg) 40%, var(--edb-dashboard-bg) 80%)",
            backgroundSize: "200% 100%",
            animation: "sk 1.1s ease-in-out infinite",
          }}
        />
      ))}
      <style>{`
        @keyframes sk { 
          0% { background-position: 200% 0; } 
          100% { background-position: -200% 0; } 
        }
      `}</style>
    </div>
  );
}

function normalizeDay(val) {
  if (!val) return "";
  const s = String(val).trim().toLowerCase();
  const map = {
    sun: "sunday",
    sunday: "sunday",
    mon: "monday",
    monday: "monday",
    tue: "tuesday",
    tues: "tuesday",
    tuesday: "tuesday",
    wed: "wednesday",
    weds: "wednesday",
    wednesday: "wednesday",
    thu: "thursday",
    thur: "thursday",
    thurs: "thursday",
    thursday: "thursday",
    fri: "friday",
    friday: "friday",
    sat: "saturday",
    saturday: "saturday",
  };
  const norm = map[s] || s;
  return norm.charAt(0).toUpperCase() + norm.slice(1);
}

/* ------------------------- Helpers for Diary mini-feed ------------------------- */
function normalizeStr(s = "") {
  return String(s || "").trim().replace(/\s+/g, " ");
}
function attachmentsSignature(arr = []) {
  if (!Array.isArray(arr)) return "";
  const norm = arr.map((a) => ({
    n: a?.originalName || a?.name || "",
    u: a?.fileUrl || a?.url || "",
    m: a?.mimeType || a?.kind || "",
    z: a?.size || 0,
  }));
  return JSON.stringify(norm.sort((a, b) => (a.n + a.u).localeCompare(b.n + b.u)));
}
function groupDiaries(items = []) {
  const byKey = new Map();
  for (const d of items) {
    if (Array.isArray(d.targets) && d.targets.length) {
      byKey.set(`targets-${d.id}`, { ...d, _sourceIds: [d.id] });
      continue;
    }
    const key = [
      (d.date || "").slice(0, 10),
      d.type,
      normalizeStr(d.title),
      normalizeStr(d.content),
      d.subjectId ?? "",
      attachmentsSignature(d.attachments),
    ].join("|");

    const entry = byKey.get(key);
    const classObj = d.class || d.Class || null;
    const sectionObj = d.section || d.Section || null;
    const target = {
      classId: d.classId || classObj?.id,
      sectionId: d.sectionId || sectionObj?.id,
      class: classObj || (d.classId ? { id: d.classId } : undefined),
      section: sectionObj || (d.sectionId ? { id: d.sectionId } : undefined),
    };

    if (!entry) {
      byKey.set(key, {
        ...d,
        targets: [target],
        _sourceIds: [d.id],
        _counts: {
          views: d.views?.length ?? d._counts?.views ?? d.seenCount ?? 0,
          acks: d.acknowledgements?.length ?? d._counts?.acks ?? d.ackCount ?? 0,
        },
      });
    } else {
      const exists = entry.targets.some((t) => t.classId === target.classId && t.sectionId === target.sectionId);
      if (!exists) entry.targets.push(target);
      entry._sourceIds.push(d.id);
      entry._counts.views += d.views?.length ?? d._counts?.views ?? d.seenCount ?? 0;
      entry._counts.acks += d.acknowledgements?.length ?? d._counts?.acks ?? d.ackCount ?? 0;
    }
  }

  return Array.from(byKey.values())
    .map((x) => ({
      ...x,
      seenCount: x._counts?.views ?? x.seenCount,
      ackCount: x._counts?.acks ?? x.ackCount,
    }))
    .sort((a, b) => new Date(b.createdAt || b.date) - new Date(a.createdAt || a.date));
}

function chipName(t) {
  const cls = t.class?.class_name || t.class?.name || t.classId || t.class?.id;
  const sec = t.section?.section_name || t.section?.name || t.sectionId || t.section?.id;
  return `Class ${cls} - Sec ${sec}`;
}
function singleChipName(d) {
  const cls =
    d.class?.class_name ||
    d.class?.name ||
    d.classId ||
    d.Class?.class_name ||
    d.Class?.name ||
    d.Class?.id ||
    d.class_id;
  const sec =
    d.section?.section_name ||
    d.section?.name ||
    d.sectionId ||
    d.Section?.section_name ||
    d.Section?.name ||
    d.Section?.id ||
    d.section_id;
  if (!cls && !sec) return "General";
  return `Class ${cls} - Sec ${sec}`;
}
function formatWhen(dateStr) {
  try {
    const m = moment(dateStr);
    if (!m.isValid()) return "";
    const diffH = moment().diff(m, "hours");
    if (diffH < 24) return m.fromNow();
    return m.format("DD MMM, HH:mm");
  } catch {
    return "";
  }
}
